import type { Project } from './project';

type ProjectSearchFields = Pick<Project, 'id' | 'name'>;

const compareProjectNames = (left: string | null | undefined, right: string | null | undefined): number =>
  (left ?? '').localeCompare(right ?? '', 'es', { sensitivity: 'base' });

export const matchesProjectSearch = (query: string, project: ProjectSearchFields): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const nameMatches = (project.name ?? '').toLowerCase().includes(normalizedQuery);
  const idMatches = String(project.id).includes(normalizedQuery);

  return nameMatches || idMatches;
};

export const findProjectById = (projects: Project[], id: number): Project | null => {
  const direct = projects.find((project) => project.id === id);
  if (direct) return direct;

  const subProject = projects.flatMap((master) => master.subProjects ?? []).find((sub) => sub.id === id);
  return subProject ?? null;
};

export const getMasterProjectIdForRoute = (projects: Project[], projectId: number): number | null => {
  const project = findProjectById(projects, projectId);
  if (!project) return null;

  return project.masterProjectId ?? project.id;
};

export const isMasterProjectId = (projects: Project[], projectId: number): boolean => {
  const project = findProjectById(projects, projectId);
  return project?.masterProjectId === null;
};

export const getMasterProjects = (projects: Project[]): Project[] =>
  projects.filter((project) => project.masterProjectId === null).sort((a, b) => compareProjectNames(a.name, b.name));

const sortProjectsByName = (projects: Project[]): Project[] =>
  [...projects].sort((a, b) => compareProjectNames(a.name, b.name) || a.id - b.id);

export const getSubProjects = (projects: Project[], masterProjectId: number | null): Project[] => {
  if (masterProjectId === null) {
    return [];
  }

  const masterProject = projects.find((project) => project.id === masterProjectId && project.masterProjectId === null);

  if (masterProject?.subProjects?.length) {
    return sortProjectsByName(masterProject.subProjects);
  }

  return sortProjectsByName(projects.filter((project) => project.masterProjectId === masterProjectId));
};
